import { CloseIcon } from "assets/icons";
import { useBoolean, useDebouncedValue } from "hooks";
import { CategoryTypes, useGetAllCategories } from "modules/categories";
import {
  ProductsListItem,
  ProductsSelector,
  useGetVariantsByIds
} from "modules/products";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Loader, Select } from "ui";

import { useSegmentPreview } from "../../apis";
import { ESegmentAppStatus, SegmentDefinition } from "../../marketing.types";
import { ChipGroup } from "../ChipGroup";

import styles from "./SegmentBuilder.module.scss";

interface SegmentBuilderProps {
  value: SegmentDefinition;
  onChange: (value: SegmentDefinition) => void;
  disabled?: boolean;
}

const APP_STATUS_OPTIONS = [
  { label: "Any", value: "any" },
  { label: "App installed", value: ESegmentAppStatus.Installed },
  { label: "No app", value: ESegmentAppStatus.NotInstalled }
];

const ORDERS_OPTIONS = [
  { label: "Any", value: "any" },
  { label: "Never ordered (prospects)", value: "never" },
  { label: "Has ordered", value: "ordered" },
  { label: "2+ orders", value: "2plus" },
  { label: "5+ orders", value: "5plus" }
];

const ORDERS_PATCHES: Record<string, Partial<SegmentDefinition>> = {
  any: { hasOrdered: undefined, minOrders: undefined },
  never: { hasOrdered: false, minOrders: undefined },
  ordered: { hasOrdered: true, minOrders: undefined },
  "2plus": { hasOrdered: undefined, minOrders: 2 },
  "5plus": { hasOrdered: undefined, minOrders: 5 }
};

const SPEND_OPTIONS = [
  { label: "Any", value: "any" },
  { label: "10 000+ FCFA", value: "10000" },
  { label: "25 000+ FCFA", value: "25000" },
  { label: "50 000+ FCFA", value: "50000" }
];

const INACTIVITY_OPTIONS = [
  { label: "Any", value: "any" },
  { label: "7+ days", value: "7" },
  { label: "30+ days", value: "30" },
  { label: "90+ days", value: "90" }
];

const ordersChipValue = (definition: SegmentDefinition): string => {
  if (definition.hasOrdered === false) return "never";
  if (definition.minOrders !== undefined)
    return definition.minOrders >= 5 ? "5plus" : "2plus";
  if (definition.hasOrdered === true) return "ordered";
  return "any";
};

const numberChipValue = (
  current: number | undefined,
  options: Array<{ value: string }>
): string => {
  const asString = String(current ?? "any");
  return options.some((option) => option.value === asString)
    ? asString
    : "any";
};

const SegmentBuilder = ({ value, onChange, disabled }: SegmentBuilderProps) => {
  const { t } = useTranslation();

  const {
    value: isPickerOpen,
    setTrue: openPicker,
    setFalse: closePicker
  } = useBoolean(false);

  const [selectedProducts, setSelectedProducts] = useState<ProductsListItem[]>(
    []
  );

  const variantIds = value.boughtProductVariantIds ?? [];

  const { categories } = useGetAllCategories();
  const categoryOptions = useMemo(
    () =>
      categories
        .filter((category) => category.type === CategoryTypes.SUB_CHILD_CATEGORY)
        .map((category) => ({ label: category.name, value: category.id })),
    [categories]
  );

  const { variants: hydratedVariants } = useGetVariantsByIds(
    variantIds.filter(
      (id) => !selectedProducts.some((product) => product.id === id)
    )
  );

  const debouncedDefinitionJson = useDebouncedValue<string>(
    JSON.stringify(value),
    500
  );
  const debouncedDefinition = useMemo(
    () => JSON.parse(debouncedDefinitionJson) as SegmentDefinition,
    [debouncedDefinitionJson]
  );
  const { count, isFetching } = useSegmentPreview(debouncedDefinition);

  const patch = (changes: Partial<SegmentDefinition>) =>
    onChange({ ...value, ...changes });

  const handleProductsChange = (products: ProductsListItem[]) => {
    setSelectedProducts(products);
    patch({
      boughtProductVariantIds: products.length
        ? products.map((product) => product.id)
        : undefined
    });
  };

  const removeVariant = (variantId: string) => {
    const products = selectedProducts.filter(
      (product) => product.id !== variantId
    );
    setSelectedProducts(products);

    const ids = variantIds.filter((id) => id !== variantId);
    patch({ boughtProductVariantIds: ids.length ? ids : undefined });
  };

  const variantNameById = (variantId: string): string =>
    selectedProducts.find((product) => product.id === variantId)?.name ??
    hydratedVariants.find((variant) => variant.id === variantId)?.name ??
    variantId;

  return (
    <div className={styles.builder}>
      <ChipGroup
        label={t("App status")}
        options={APP_STATUS_OPTIONS}
        value={value.appStatus ?? "any"}
        disabled={disabled}
        onChange={(selected) =>
          patch({
            appStatus:
              selected === "any" ? undefined : (selected as ESegmentAppStatus)
          })
        }
      />

      <ChipGroup
        label={t("Past orders")}
        options={ORDERS_OPTIONS}
        value={ordersChipValue(value)}
        disabled={disabled}
        onChange={(selected) => patch(ORDERS_PATCHES[selected])}
      />

      <ChipGroup
        label={t("Total order value")}
        options={SPEND_OPTIONS}
        value={numberChipValue(value.minTotalSpend, SPEND_OPTIONS)}
        disabled={disabled}
        onChange={(selected) =>
          patch({
            minTotalSpend: selected === "any" ? undefined : Number(selected)
          })
        }
      />

      <ChipGroup
        label={t("Inactivity")}
        options={INACTIVITY_OPTIONS}
        value={numberChipValue(value.inactiveForDays, INACTIVITY_OPTIONS)}
        disabled={disabled}
        onChange={(selected) =>
          patch({
            inactiveForDays: selected === "any" ? undefined : Number(selected)
          })
        }
      />

      <div className={styles.builder__products}>
        <span className={styles.builder__label}>
          {t("Bought specific products")}
        </span>

        <div className={styles.builder__productsRow}>
          {variantIds.map((variantId) => (
            <span key={variantId} className={styles.builder__chip}>
              {variantNameById(variantId)}

              {!disabled && (
                <button
                  type="button"
                  className={styles.builder__chipRemove}
                  onClick={() => removeVariant(variantId)}
                >
                  <CloseIcon width={12} height={12} />
                </button>
              )}
            </span>
          ))}

          {!disabled && (
            <Button variant="dashed" size="sm" onClick={openPicker}>
              {variantIds.length ? t("Change products") : t("+ Pick products")}
            </Button>
          )}
        </div>
      </div>

      <Select
        label={t("Bought in categories")}
        placeholder={t("Any category")}
        multiple
        withFilterInput
        disabled={disabled}
        value={value.boughtCategoryIds ?? []}
        options={categoryOptions}
        onChange={(selected) => {
          const ids = selected as string[];
          patch({ boughtCategoryIds: ids.length ? ids : undefined });
        }}
      />

      <div className={styles.builder__estimate}>
        <div className={styles.builder__estimateMain}>
          {isFetching ? (
            <Loader size={20} />
          ) : (
            <>
              <span className={styles.builder__count}>{count ?? 0}</span>
              <span className={styles.builder__countLabel}>
                {t("contacts")}
              </span>
            </>
          )}
        </div>

        <div className={styles.builder__estimateSide}>
          <span className={styles.builder__estimateHint}>
            {t("Estimate updates live")} ·{" "}
            {t("opted-out contacts are excluded")}
          </span>
        </div>
      </div>

      <ProductsSelector
        isOpen={isPickerOpen}
        onClose={closePicker}
        value={selectedProducts}
        onChange={handleProductsChange}
        title={t("Pick bought products")}
      />
    </div>
  );
};

export default SegmentBuilder;
